import { createFileRoute } from "@tanstack/react-router";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { breadcrumbs, faqPage, qaPage, speakable, aeoGeo } from "@/lib/seo/schema";

const PATH = "/ai-i-filmproduktion";
const ANSWER =
  "AI i filmproduktion fungerar 2026 bäst som verktyg i delmoment — textning, översättning, transkribering, grovklipp, idéskisser och AI-röst för internt material. Helt AI-genererad film passar snabba interna klipp och tester, men har fortfarande brister i trovärdighet, varumärkeskontroll och rättigheter jämfört med producerad film.";

const FAQ: LandingFAQ[] = [
  { q: "Kan AI göra en reklamfilm?", a: "AI kan generera klipp, bilder och röst, men en reklamfilm som ska bära ett varumärke kräver fortfarande styrning av idé, casting, bildspråk och rättigheter. AI fungerar bäst som verktyg i produktionen, inte som ersättning för den." },
  { q: "Vad används AI till i filmproduktion?", a: "Främst textning, översättning, transkribering av intervjuer, grovklipp, research, idéskisser och storyboard-utkast samt AI-röst för internt material och många språkversioner." },
  { q: "Vem äger en AI-genererad film?", a: "Upphovsrätt till rent AI-genererat material är juridiskt oklar och villkoren skiljer sig mellan verktyg. Kontrollera alltid licensvillkoren innan materialet används kommersiellt." },
  { q: "Är AI-film billigare?", a: "Ofta billigare per klipp, men inte alltid per resultat. Tid för styrning, rättning och godkännande äter upp delar av besparingen, och varumärkesrisken är svårare att prissätta." },
];

const TITLE = "AI i filmproduktion 2026 — vad fungerar och vad gör det inte";
const DESC =
  "Så används AI i filmproduktion 2026: textning, översättning, grovklipp och AI-röst. Jämförelse mellan AI-genererad och producerad film — kostnad, kvalitet, rättigheter och risk.";

const ROWS: [string, string, string][] = [
  ["Kostnad per klipp", "Låg", "Högre"],
  ["Trovärdighet med riktiga människor", "Svag", "Stark"],
  ["Varumärkeskontroll", "Begränsad", "Full"],
  ["Rättigheter för kommersiellt bruk", "Oklara, beror på verktyg", "Tydliga, avtalas"],
  ["Snabba interna klipp och tester", "Passar bra", "Ofta överdimensionerat"],
  ["Kundcase och rekrytering", "Passar dåligt", "Passar bra"],
];

export const Route = createFileRoute("/ai-i-filmproduktion")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `https://fenny.se${PATH}` },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: `https://fenny.se${PATH}` }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path: PATH, name: TITLE, description: DESC })) },
      { type: "application/ld+json", children: JSON.stringify(qaPage({ question: "Hur används AI i filmproduktion?", answer: ANSWER, path: PATH, authorName: "Fenny" })) },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Kunskap", path: "/kunskap" }, { name: "AI i filmproduktion", path: PATH }])) },
    ],
  }),
  component: Page,
});

const a = "underline decoration-mint/40 underline-offset-4 hover:text-mint";

function Page() {
  return (
    <>
      <LandingPage
        eyebrow="Guide — AI film"
        title={<>AI i filmproduktion 2026: vad fungerar och vad gör det inte</>}
        lede={ANSWER}
        tldr={<p>AI är bäst på <strong>textning, översättning, grovklipp och AI-röst</strong>. Producerad film vinner på <strong>trovärdighet, varumärkeskontroll och tydliga rättigheter</strong>.</p>}
        ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
        ctaSecondary={{ label: "Se Pulse", to: "/pulse" }}
        trackingId="ai-i-filmproduktion"
        sections={[
          {
            id: "dar-ai-fungerar",
            heading: "Där AI fungerar bra",
            body: (
              <p>
                <strong>Textning och översättning</strong> av färdig film till flera språk.{" "}
                <strong>Transkribering</strong> av intervjuer så att klipparen hittar rätt citat snabbare.{" "}
                <strong>Idéskisser och storyboard-utkast</strong> i förproduktionen.{" "}
                <strong>AI-röst</strong> för internt material och utbildning — se{" "}
                <a className={a} href="/jamfor/voice-over-vs-ai-rost">speaker eller AI-röst</a>.
              </p>
            ),
          },
          {
            id: "dar-ai-brister",
            heading: "Där AI fortfarande brister",
            body: (
              <p>
                Film som bygger på förtroende — <a className={a} href="/kundcase-film">kundcase</a>,{" "}
                <a className={a} href="/rekryteringsfilm">rekrytering</a> och varumärke — kräver riktiga
                människor och riktiga platser. Genererade personer och miljöer uppfattas ofta som
                konstlade, och ett varumärke som syns i fel sammanhang är svårt att rätta i efterhand.
              </p>
            ),
          },
          {
            id: "jamforelse",
            heading: "AI-genererad film vs producerad film",
            body: (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[520px] text-left text-sm">
                  <thead className="border-b border-border text-muted-foreground">
                    <tr><th className="py-2 pr-4 font-medium">Kriterium</th><th className="py-2 pr-4 font-medium">AI-genererad</th><th className="py-2 font-medium">Producerad</th></tr>
                  </thead>
                  <tbody>
                    {ROWS.map(([k, x, y]) => (
                      <tr key={k} className="border-b border-border/60"><td className="py-2 pr-4">{k}</td><td className="py-2 pr-4 text-muted-foreground">{x}</td><td className="py-2 text-muted-foreground">{y}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ),
          },
          {
            id: "rattigheter",
            heading: "Rättigheter och risk",
            body: (
              <p>
                Upphovsrätten till rent AI-genererat material är juridiskt oklar, och verktygens
                licensvillkor skiljer sig åt. Läs villkoren innan materialet används kommersiellt, och
                ladda aldrig upp konfidentiellt kundmaterial i verktyg som tränar på det. Vid{" "}
                <a className={a} href="/upphandling-film">upphandling av film</a> är det rimligt att
                fråga leverantören hur AI används.
              </p>
            ),
          },
        ]}
        faq={FAQ}
        closing={{
          title: "Behöver ni film som håller för varumärket?",
          body: "Berätta vad filmen ska göra så föreslår vi rätt nivå.",
          primary: { label: "Boka strategimöte", to: "/kontakt" },
        }}
      />
      <RelatedKnowledgeAuto topics={["process", "pris"]} />
    </>
  );
}
